import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import ScrollToTop from '../common/ScrollToTop';
import { projectService } from '../../services/projectService';
import { getStudyRegistrationByProject, upsertStudyRegistration } from '../../services/studyRegistration.service';
import { studyRegistrationToAdminFields } from '../../utils/studyRegistrationToAdminFields';
import {
  getProjectIdentityFields,
  PROJECT_IDENTITY_SOURCE_HINT,
  resolveProjectSponsorName,
  resolveProjectSponsorProtocolId,
  resolveProjectTrialAcronym,
  validateRequiredProjectIdentity,
  withProjectIdentityFields,
  formatProjectDuplicateError,
} from '../../utils/projectIdentityFields';
import StudyTeamForm from './StudyTeamForm';

const ORIGINAL_YES = 'C49488';
const ORIGINAL_NO = 'C49487';

// The 15 fixed stages from Standard_Version_Labels.pdf — kept in sync with
// StudyRegistration.js's VERSION_LABELS static on the server.
const VERSION_LABELS = [
  'Initial Draft — AI-Generated',
  'Draft in Progress',
  'Internal Review / Validation',
  'Sent for Team Review — Draft 1',
  'Team Review Complete — Draft 1',
  'Incorporating Comments — Draft 1',
  'Updated Draft 1 — Redline',
  'Updated Draft 2 — Clean',
  'Sent for Team Review — Draft 2',
  'Team Review Complete — Draft 2',
  'Incorporating Comments — Draft 2',
  'Updated Draft 2 — Redline',
  'Final Draft — Clean',
  'Sent for Approval',
  'Approved / Final',
];

const isValidVersionNumber = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return true;
  if (!/^\d+(\.\d+)?$/.test(s)) return false;
  const n = Number(s);
  return !Number.isNaN(n) && n > 0 && n <= 1;
};

const isVersionNumberInputAllowed = (raw) => {
  const s = String(raw);
  if (s === '') return true;
  if (s.startsWith('-') || !/^\d*\.?\d*$/.test(s)) return false;
  const n = Number(s);
  if (!Number.isNaN(n) && n > 1) return false;
  return true;
};

const formatVersionNumberFromApi = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const s = String(value).trim();
  if (!isValidVersionNumber(s)) return s;
  const n = Number(s);
  if (n === 1) return '1';
  return String(parseFloat(n.toFixed(4)));
};

/** Investigational molecule / drug project name (parent project), not trial acronym. */
function projectDrugNameFromRecord(project) {
  if (!project) return '';
  const drug = project.drug;
  if (drug && typeof drug === 'object' && drug !== null && String(drug.name || '').trim()) {
    return String(drug.name).trim();
  }
  return '';
}

/**
 * @param {{ projectId: string, onMergedToFormData?: (partial: object) => void }} props
 */
const StudyRegistrationForm = ({ projectId, onMergedToFormData }) => {
  const [loadingProject, setLoadingProject] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [projectRecord, setProjectRecord] = useState(null);
  const [form, setForm] = useState({
    sponsor_name: '',
    trial_acronym: '',
    original_protocol_indicator: '',
    amendment_identifier: '',
    amendment_scope: '',
    version_number: '',
    version_date: '',
    version_label: '',
    version_summary: '',
    system_unique_study_id: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadAll = async () => {
    if (!projectId) {
      setLoadingProject(false);
      return;
    }
    setLoadingProject(true);
    setHasSaved(false);
    try {
      const [projRes, regRes] = await Promise.all([
        projectService.getProject(projectId),
        getStudyRegistrationByProject(projectId).catch(() => ({ data: null })),
      ]);
      const p = projRes?.data != null ? projRes.data : projRes;
      setProjectRecord(p && typeof p === 'object' ? p : null);
      const reg = regRes?.data ?? null;
      setForm({
        sponsor_name: (resolveProjectSponsorName(p) || reg?.sponsor_name) ?? '',
        trial_acronym: resolveProjectTrialAcronym(p) ?? '',
        original_protocol_indicator: reg?.original_protocol_indicator ?? '',
        amendment_identifier: reg?.amendment_identifier ?? '',
        amendment_scope: reg?.amendment_scope ?? '',
        version_number: formatVersionNumberFromApi(reg?.version_number),
        version_date: reg?.version_date ? new Date(reg.version_date).toISOString().slice(0, 10) : '',
        version_label: reg?.version_label ?? '',
        version_summary: reg?.version_summary ?? '',
        system_unique_study_id: reg?.system_unique_study_id ?? '',
      });
      // Already persisted for this project — keep Save greyed out
      if (reg && (reg._id || reg.system_unique_study_id || reg.original_protocol_indicator)) {
        setHasSaved(true);
      }
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: e.message || 'Failed to load study registration', severity: 'error' });
    } finally {
      setLoadingProject(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'version_number') {
      if (!isVersionNumberInputAllowed(value)) return;
      setHasSaved(false);
      setForm((prev) => ({ ...prev, version_number: value }));
      return;
    }
    // Any successful edit after save should re-enable Save.
    setHasSaved(false);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId) {
      setSnackbar({ open: true, message: 'Open a project (?project=) first.', severity: 'warning' });
      return;
    }
    if (!form.original_protocol_indicator) {
      setSnackbar({ open: true, message: 'Select Original protocol indicator (Yes / No).', severity: 'warning' });
      return;
    }
    if (form.original_protocol_indicator === ORIGINAL_NO) {
      if (!form.amendment_identifier?.trim()) {
        setSnackbar({ open: true, message: 'Amendment identifier is required when the study is not an original protocol.', severity: 'warning' });
        return;
      }
      if (!form.amendment_scope) {
        setSnackbar({ open: true, message: 'Amendment scope is required when the study is not an original protocol.', severity: 'warning' });
        return;
      }
      if (!form.version_number?.trim()) {
        setSnackbar({ open: true, message: 'Version number is required for an amendment.', severity: 'warning' });
        return;
      }
      if (!form.version_date) {
        setSnackbar({ open: true, message: 'Version date is required for an amendment.', severity: 'warning' });
        return;
      }
    }
    if (form.version_number && !isValidVersionNumber(form.version_number)) {
      setSnackbar({
        open: true,
        message: 'Version number must be a decimal greater than 0 and not greater than 1 (e.g. 0.11, 0.5, 1).',
        severity: 'warning',
      });
      return;
    }
    // Sponsor Protocol Identifier is validated at the trial level (fetched, read-only here).
    // Sponsor Name is optional at trial creation but required from this point on.
    const identityErrors = validateRequiredProjectIdentity({
      sponsorProtocolIdentifier: resolveProjectSponsorProtocolId(projectRecord),
    });
    if (!form.sponsor_name.trim()) {
      identityErrors.push('Sponsor Name is required.');
    }
    if (identityErrors.length > 0) {
      setSnackbar({ open: true, message: identityErrors.join(' '), severity: 'warning' });
      return;
    }

    setSaving(true);
    try {
      // Sponsor Name / Trial Acronym are canonically stored on the Project record.
      // If the user edited them here, persist to the Project first so this save
      // (and every other screen that reads project identity) reflects the change.
      let effectiveProjectRecord = projectRecord;
      const identityChanged =
        form.sponsor_name.trim() !== resolveProjectSponsorName(projectRecord) ||
        form.trial_acronym.trim() !== resolveProjectTrialAcronym(projectRecord);
      if (identityChanged) {
        const updateRes = await projectService.updateProject(projectId, {
          sponsorProtocolIdentifier: resolveProjectSponsorProtocolId(projectRecord),
          trialAcronym: form.trial_acronym.trim(),
          sponsorName: form.sponsor_name.trim(),
        });
        const updated = updateRes?.data ?? updateRes;
        if (updated && typeof updated === 'object') {
          effectiveProjectRecord = updated;
          setProjectRecord(updated);
        }
      }

      const payload = withProjectIdentityFields(effectiveProjectRecord, {
        projectId,
        sponsor_name: form.sponsor_name,
        original_protocol_indicator: form.original_protocol_indicator,
        amendment_identifier: form.amendment_identifier,
        amendment_scope: form.amendment_scope,
        version_number: form.version_number
          ? Number(formatVersionNumberFromApi(form.version_number))
          : undefined,
        version_date: form.version_date || undefined,
        version_label: form.version_label || '',
        version_summary: form.version_summary || '',
      });
      const res = await upsertStudyRegistration(payload);
      const saved = res?.data;
      if (saved) {
        setForm((prev) => ({
          ...prev,
          system_unique_study_id: saved.system_unique_study_id ?? prev.system_unique_study_id,
          version_number: formatVersionNumberFromApi(saved.version_number),
          version_date: saved.version_date
            ? new Date(saved.version_date).toISOString().slice(0, 10)
            : '',
          version_label: saved.version_label ?? prev.version_label,
          version_summary: saved.version_summary ?? prev.version_summary,
          amendment_identifier: saved.amendment_identifier ?? prev.amendment_identifier,
          amendment_scope: saved.amendment_scope ?? prev.amendment_scope,
          original_protocol_indicator:
            saved.original_protocol_indicator ?? prev.original_protocol_indicator,
        }));
      }
      if (onMergedToFormData) {
        onMergedToFormData(studyRegistrationToAdminFields(saved, effectiveProjectRecord));
      }
      setHasSaved(true);
      setSnackbar({
        open: true,
        message: res?.administrativeInfoSynced
          ? 'Study registration saved. Administrative information was updated for the title page and exports.'
          : 'Study registration saved.',
        severity: 'success',
      });
    } catch (err) {
      const message = err?.response?.data?.code === 'DUPLICATE_PROJECT'
        ? formatProjectDuplicateError(err)
        : err.response?.data?.message || err.message || 'Save failed';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!projectId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Select or open a project (URL <code>?project=...</code>) to use Study Registration.</Alert>
      </Box>
    );
  }

  if (loadingProject) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
        <CircularProgress size={28} />
        <Typography>Loading study registration…</Typography>
      </Box>
    );
  }

  const showAmendment = form.original_protocol_indicator === ORIGINAL_NO;

  return (
    <Box sx={{ mt: 2, maxWidth: 960 }}>
      <Typography variant="h6" gutterBottom>
        Study registration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Capture core identifiers for this project. When <strong>Original protocol</strong> is <strong>No</strong>,
        amendment fields are required here and are merged into <strong>Administrative Info</strong> for the protocol
        title page and related fields. {PROJECT_IDENTITY_SOURCE_HINT}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Sponsor Protocol Identifier"
            value={resolveProjectSponsorProtocolId(projectRecord)}
            InputProps={{ readOnly: true }}
            helperText="From trial creation (Projects sidebar → Edit Trial)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Trial Acronym"
            name="trial_acronym"
            value={form.trial_acronym}
            onChange={handleChange}
            helperText="Optional. Updates the trial record used across the app."
          />
        </Grid>
        {projectDrugNameFromRecord(projectRecord) && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Project Name (molecule)"
              value={projectDrugNameFromRecord(projectRecord)}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Sponsor Name"
            name="sponsor_name"
            value={form.sponsor_name}
            onChange={handleChange}
            helperText="Updates the trial record used across the app."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel id="orig-proto-label">Original protocol indicator</InputLabel>
            <Select
              labelId="orig-proto-label"
              label="Original protocol indicator"
              name="original_protocol_indicator"
              value={form.original_protocol_indicator}
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>Select…</em>
              </MenuItem>
              <MenuItem value={ORIGINAL_YES}>Yes (original protocol)</MenuItem>
              <MenuItem value={ORIGINAL_NO}>No (amendment)</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary">
            Version Control
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Version number"
            name="version_number"
            value={form.version_number}
            onChange={handleChange}
            onBlur={() => {
              if (!form.version_number) return;
              if (!isValidVersionNumber(form.version_number)) return;
              setForm((prev) => ({
                ...prev,
                version_number: formatVersionNumberFromApi(prev.version_number),
              }));
            }}
            placeholder="e.g. 0.11, 0.5, 0.99, 1"
            inputProps={{ inputMode: 'decimal' }}
            helperText="Decimal greater than 0 up to 1 (e.g. 0.11, 1)."
            error={Boolean(form.version_number && !isValidVersionNumber(form.version_number))}
            required={showAmendment}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Version date"
            name="version_date"
            type="date"
            value={form.version_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            required={showAmendment}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="version-label-label">Label</InputLabel>
            <Select
              labelId="version-label-label"
              label="Label"
              name="version_label"
              value={form.version_label}
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {VERSION_LABELS.map((label) => (
                <MenuItem key={label} value={label}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Summary"
            name="version_summary"
            value={form.version_summary}
            onChange={handleChange}
            placeholder="Brief description of what changed in this version"
          />
        </Grid>

        {showAmendment && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary">
                Amendment details (also applied to Administrative Info)
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Amendment identifier"
                name="amendment_identifier"
                value={form.amendment_identifier}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="amd-scope-label">Amendment scope</InputLabel>
                <Select
                  labelId="amd-scope-label"
                  label="Amendment scope"
                  name="amendment_scope"
                  value={form.amendment_scope}
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>Select…</em>
                  </MenuItem>
                  <MenuItem value="C68846">Global</MenuItem>
                  <MenuItem value="CNEW">Not Global</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || hasSaved}
            sx={hasSaved ? { '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' } } : undefined}
          >
            {saving ? <CircularProgress size={22} /> : hasSaved ? 'Study registration saved' : 'Save study registration'}
          </Button>
          {hasSaved && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Saved. Change any field above to enable Save again.
            </Typography>
          )}
        </Grid>
      </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      <StudyTeamForm projectId={projectId} embedded />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ScrollToTop />
    </Box>
  );
};

export default StudyRegistrationForm;
